import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, StatusChip } from '@/components/core';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { TimeSheetRecord } from '../../types/timesheet-manager.types';

const formatHours = (hours: number) => `${hours.toFixed(2)}h`;

const totalBillable = (record: TimeSheetRecord) =>
  record.Tasks.reduce((sum, task) => sum + task.Billable, 0);
const totalNonBillable = (record: TimeSheetRecord) =>
  record.Tasks.reduce((sum, task) => sum + task.NonBillable, 0);

interface CreateTimesheetEntriesTableColumnsProps {
  t: (key: string) => string;
}

/** Read-only listing of raw TimeSheet entries -- used on the manager's per-user drill-down page. */
export const createTimesheetEntriesTableColumns = ({
  t,
}: CreateTimesheetEntriesTableColumnsProps): ColumnDef<TimeSheetRecord>[] => [
  {
    id: 'TaskDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DATE')} />,
    accessorFn: (row) => row.TaskDate,
    cell: ({ row }) => <span>{formatDateOnly(row.original.TaskDate)}</span>,
  },
  {
    id: 'TaskCount',
    // Counted from the tasks JSON after the page loads; not a field the server can sort on.
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_TASKS')} />
    ),
    accessorFn: (row) => row.Tasks.length,
  },
  {
    id: 'TotalBillable',
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_BILLABLE_HOURS')} />
    ),
    accessorFn: (row) => totalBillable(row),
    cell: ({ row }) => <span>{formatHours(totalBillable(row.original))}</span>,
  },
  {
    id: 'TotalNonBillable',
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_NON_BILLABLE_HOURS')} />
    ),
    accessorFn: (row) => totalNonBillable(row),
    cell: ({ row }) => <span>{formatHours(totalNonBillable(row.original))}</span>,
  },
  {
    id: 'IsUnderTime',
    // Card: the status chip top right.
    meta: {
      mobile: 'badge',
      filter: {
        type: 'options',
        options: [
          { value: true, label: t('TIMESHEET_MANAGER_UNDER_TIME') },
          { value: false, label: t('TIMESHEET_MANAGER_ON_TARGET') },
        ],
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.IsUnderTime,
    cell: ({ row }) =>
      row.original.IsUnderTime ? (
        <StatusChip tone="error">{t('TIMESHEET_MANAGER_UNDER_TIME')}</StatusChip>
      ) : (
        <StatusChip tone="success">{t('TIMESHEET_MANAGER_ON_TARGET')}</StatusChip>
      ),
  },
];
